import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GetPostsByUserIdAndStatus } from "../../../services/PostService";
import { useSelector } from "react-redux";
import { useGraphQL } from "../../../context/GraphQLProvider";
import DashboardMainContainer from "../../../components/common/Containers/DashboardMainContainer";
import DashboardAdsCard from "../../../components/common/AdsCard/DashboardAdsCard";
import {
  AdminPageTitle,
  AdminSupportingText,
  AdminHeaderAction,
} from "../../../sections/AdminDashboard/shared/adminUx";
import { BUSINESS_CENTERED_PAGE_TITLE_CLASS, BUSINESS_POST_GRID_CLASS, BUSINESS_LIST_FOOTER_CLASS } from "../../../sections/BusinessDashboard/shared/businessLayoutSpec";
import { UpdatePostStatusMutation } from "../../../services/PostService";
import Alerts from "../../../components/common/Alerts/Alerts";
import { emitPublicationActivity } from "../../../data/changeHistoryEmitters";

const Published = () => {
  const { user: userId } = useSelector((state) => state.user);
  const graphqlService = useGraphQL();
  const [allPublished, setAllPublished] = useState([]);
  const [displayedPosts, setDisplayedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [deletingPostId, setDeletingPostId] = useState(null);
  const navigate = useNavigate();

  const fetchPublished = async () => {
    if (userId) {
      setIsLoading(true);
      try {
        const response = await GetPostsByUserIdAndStatus(graphqlService, {
          userId: parseFloat(userId.id),
          status: "published"
        });
        const posts = response?.getPostsByStatus || [];
        setAllPublished(posts);
        // Show first 9 posts by default
        setDisplayedPosts(posts.slice(0, 9));
      } catch (error) {
        console.error("Error fetching published posts:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchPublished();
  }, []);

  const handleLoadMore = () => {
    setIsLoadingMore(true);
    // Simulate loading delay for better UX
    setTimeout(() => {
      const currentCount = displayedPosts.length;
      const nextBatch = allPublished.slice(currentCount, currentCount + 6);
      setDisplayedPosts(prev => [...prev, ...nextBatch]);
      setIsLoadingMore(false);
    }, 500);
  };

  const handleEdit = (postId) => {
    navigate(`/business-dashboard/posts/edit/${postId}`);
  };

  const handleDelete = async (postId) => {
    setDeletingPostId(postId);
    const post = allPublished.find((row) => row.id === postId);
    try {
      await UpdatePostStatusMutation(graphqlService, {
        id: postId,
        status: "trash"
      });
      try {
        emitPublicationActivity({
          targetType: 'Post',
          targetId: postId,
          targetName: post?.postTitle || post?.title || postId,
          actionLabel: 'Take Down',
          outcome: 'Post moved to recycle bin',
          actor: 'Business',
        });
      } catch {
        /* history recording failure must not reverse the source save */
      }
      // Remove from both arrays
      setAllPublished(prev => prev.filter(post => post.id !== postId));
      setDisplayedPosts(prev => prev.filter(post => post.id !== postId));
    } catch (error) {
      console.error("Error moving post to trash:", error);
      Alerts.error("We couldn't move your post to the trash bin. Please try again later.");
    } finally {
      setDeletingPostId(null);
    }
  };

  const hasMorePosts = displayedPosts.length < allPublished.length;

  if (isLoading) {
    return (
      <DashboardMainContainer className={"pb-10 !bg-white"}>
        <AdminPageTitle className={BUSINESS_CENTERED_PAGE_TITLE_CLASS}>Published posts</AdminPageTitle>
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primaryblue"></div>
        </div>
      </DashboardMainContainer>
    );
  }

  return (
    <DashboardMainContainer className={"pb-10 !bg-white"}>
      <AdminPageTitle className={BUSINESS_CENTERED_PAGE_TITLE_CLASS}>Published posts</AdminPageTitle>
      
      {displayedPosts?.length === 0 ? (
        <AdminSupportingText className="text-center text-mediumgray block">
          No published posts found
        </AdminSupportingText>
      ) : (
        <div>
          {/* Posts Grid */}
          <div className={BUSINESS_POST_GRID_CLASS}>
            {displayedPosts.map((post) => (
              <div 
                key={post.id} 
                className={`relative ${deletingPostId === post.id ? 'opacity-50 pointer-events-none' : ''}`}
              >
                {deletingPostId === post.id && (
                  <div className="absolute inset-0 flex items-center justify-center z-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primaryblue"></div>
                  </div>
                )}
                <DashboardAdsCard
                  saveButton={false}
                  additionalActions={true}
                  editButton={true}
                  previewButton={true}
                  editOnClick={() => handleEdit(post.id)}
                  deleteButton={true}
                  deleteOnClick={() => handleDelete(post.id)}
                  postTitle={post.postTitle}
                  serviceDescription={post.postDescription}
                  category={post.mainCategory}
                  subCategory={post.subCategory}
                  Images={post.content ? JSON.parse(post.content) : []}
                  serviceId={post.id}
                  companyId={post.userId}
                  postKeywords={post.postKeywords}
                  postRadius={post.postRadius}
                  postSuburb={post.postSuburb}
                  isHighlighted={post.isHighlighted}
                  likes={post.likes}
                  views={post.views}
                />
              </div>
            ))}
          </div>

          {/* Load More Button */}
          {hasMorePosts && (
            <div className={BUSINESS_LIST_FOOTER_CLASS}>
              <AdminHeaderAction
                onClick={handleLoadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Loading...
                  </span>
                ) : (
                  "Load more"
                )}
              </AdminHeaderAction>
            </div>
          )}
        </div>
      )}
    </DashboardMainContainer>
  );
};

export default Published;
